import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EventNoteRoundedIcon from "@material-ui/icons/EventNoteRounded";
import PersonRoundedIcon from "@material-ui/icons/PersonRounded";
import EditRoundedIcon from "@material-ui/icons/EditRounded";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { toast } from "react-toastify";
import { format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import useEstilosModal from "./estilosModal";
import { rotuloStatus } from "./cores";

const paraCampos = (h) => {
  const ini = new Date(h.startAt);
  const fim = new Date(h.endAt);
  return {
    data: format(ini, "yyyy-MM-dd"),
    horaInicio: format(ini, "HH:mm"),
    horaFim: format(fim, "HH:mm"),
    title: h.title || "",
    location: h.location || "",
    interviewerId: h.interviewerId || "",
    notes: h.notes || "",
    candidateName: h.candidateName || "",
    candidatePhone: h.candidatePhone || "",
    jobTitle: h.jobTitle || "",
    candidateNotes: h.candidateNotes || "",
  };
};

const juntar = (data, hora) => {
  const [a, m, d] = data.split("-").map(Number);
  const [h, mi] = hora.split(":").map(Number);
  return new Date(a, m - 1, d, h, mi);
};

const DetalheHorarioModal = ({ horario, usuarios, podeEditar, onClose, onAlterado }) => {
  const classes = useEstilosModal();
  const history = useHistory();
  const [campos, setCampos] = useState(null);
  const [modo, setModo] = useState("ver"); // ver | editar | agendar | cancelar
  const [avisar, setAvisar] = useState(true);
  const [mensagem, setMensagem] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [confirmarExclusao, setConfirmarExclusao] = useState(false);

  useEffect(() => {
    if (horario) {
      setCampos(paraCampos(horario));
      setModo("ver");
      setAvisar(true);
      setMensagem("");
    }
    // so quando troca de horario, nao a cada atualizacao do mesmo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horario?.id]);

  if (!horario || !campos) return null;

  const mudar = (campo) => (e) => {
    const valor = e.target.value;
    setCampos((c) => ({ ...c, [campo]: valor }));
  };

  const executar = async (corpo, sucesso) => {
    setSalvando(true);
    try {
      await api.put(`/interviews/${horario.id}`, corpo);
      toast.success(sucesso);
      onAlterado();
      return true;
    } catch (err) {
      toastError(err);
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const salvarEdicao = async () => {
    const inicio = juntar(campos.data, campos.horaInicio);
    const fim = juntar(campos.data, campos.horaFim);
    const mudouHorario =
      inicio.getTime() !== new Date(horario.startAt).getTime() ||
      fim.getTime() !== new Date(horario.endAt).getTime();
    const ok = await executar(
      {
        startAt: inicio.toISOString(),
        endAt: fim.toISOString(),
        title: campos.title,
        location: campos.location,
        interviewerId: campos.interviewerId || null,
        notes: campos.notes,
        ...(horario.status === "booked"
          ? {
              candidateName: campos.candidateName,
              candidatePhone: campos.candidatePhone,
              jobTitle: campos.jobTitle,
              candidateNotes: campos.candidateNotes,
              notify: mudouHorario && avisar && !!horario.ticketId,
            }
          : {}),
      },
      mudouHorario && horario.status === "booked" && avisar && horario.ticketId
        ? "Entrevista remarcada e candidato avisado."
        : "Horário atualizado."
    );
    if (ok) setModo("ver");
  };

  const agendarManual = async () => {
    const ok = await executar(
      {
        action: "book",
        candidateName: campos.candidateName,
        candidatePhone: campos.candidatePhone,
        jobTitle: campos.jobTitle,
        candidateNotes: campos.candidateNotes,
      },
      "Entrevista agendada."
    );
    if (ok) onClose();
  };

  const cancelarEntrevista = async () => {
    const ok = await executar(
      { action: "cancel", notify: avisar && !!horario.ticketId, message: mensagem },
      "Entrevista cancelada. O horário voltou a ficar livre."
    );
    if (ok) onClose();
  };

  const excluir = async () => {
    try {
      await api.delete(`/interviews/${horario.id}`);
      toast.success("Horário excluído.");
      onAlterado();
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  const ini = new Date(horario.startAt);
  const fim = new Date(horario.endAt);
  const marcado = horario.status === "booked";
  const passou = fim.getTime() < Date.now();
  // Resultado ("veio ou faltou") pode ser marcado a partir de 15 min antes do
  // inicio — antes so aparecia depois do FIM da entrevista.
  const podeMarcarResultado = ini.getTime() - 15 * 60 * 1000 <= Date.now();
  const nomeEntrevistador =
    horario.interviewer?.name || (usuarios || []).find((u) => u.id === horario.interviewerId)?.name;

  const Info = ({ rotulo, children }) =>
    children ? (
      <div className={classes.linhaInfo}>
        <b>{rotulo}</b>
        <span className={classes.textoLongo}>{children}</span>
      </div>
    ) : null;

  const camposDoCandidato = (
    <>
      <div className={classes.grade}>
        <TextField
          label="Nome do candidato"
          size="small"
          variant="outlined"
          className={classes.campo}
          value={campos.candidateName}
          onChange={mudar("candidateName")}
          required
        />
        <TextField
          label="Telefone"
          size="small"
          variant="outlined"
          className={classes.campo}
          value={campos.candidatePhone}
          onChange={mudar("candidatePhone")}
        />
      </div>
      <TextField
        label="Vaga"
        size="small"
        variant="outlined"
        fullWidth
        className={classes.campo}
        style={{ marginTop: 12 }}
        value={campos.jobTitle}
        onChange={mudar("jobTitle")}
      />
      <TextField
        label="Observações sobre o candidato"
        size="small"
        variant="outlined"
        fullWidth
        multiline
        minRows={2}
        className={classes.campo}
        style={{ marginTop: 12 }}
        value={campos.candidateNotes}
        onChange={mudar("candidateNotes")}
      />
    </>
  );

  return (
    <Dialog open={!!horario} onClose={onClose} maxWidth={false} PaperProps={{ className: classes.papel }}>
      <div className={classes.hero}>
        <div className={classes.heroLinha}>
          <div className={classes.heroIcone}>{marcado ? <PersonRoundedIcon /> : <EventNoteRoundedIcon />}</div>
          <div style={{ minWidth: 0 }}>
            <div className={classes.heroEtiqueta}>{rotuloStatus(horario.status)}</div>
            <div className={classes.heroTitulo}>
              {marcado ? horario.candidateName || "Candidato" : horario.title || "Horário livre"}
            </div>
            <div className={classes.heroSub}>
              {(() => {
                const t = format(ini, "EEEE, d 'de' MMMM", { locale: ptBR });
                return t.charAt(0).toUpperCase() + t.slice(1);
              })()}{" "}
              · {format(ini, "HH:mm")}–{format(fim, "HH:mm")}
            </div>
            {marcado && (
              <span className={classes.chip}>
                {horario.bookedBy === "IA" ? "🤖 Agendada pela assistente" : `Agendada por ${horario.bookedBy || "RH"}`}
              </span>
            )}
          </div>
          <IconButton size="small" className={classes.fechar} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className={classes.corpo}>
        {modo === "ver" && (
          <>
            {marcado && (
              <div className={classes.secao}>
                <div className={classes.secaoTopo}>
                  <div className={classes.secaoIcone}>
                    <PersonRoundedIcon />
                  </div>
                  <div className={classes.secaoTitulo}>Candidato</div>
                </div>
                <Info rotulo="Nome">{horario.candidateName}</Info>
                <Info rotulo="Telefone">{horario.candidatePhone || horario.contact?.number}</Info>
                <Info rotulo="Vaga">{horario.jobTitle}</Info>
                <Info rotulo="Triagem">{horario.candidateNotes}</Info>
                {horario.reminderSentAt && (
                  <Info rotulo="Lembrete">
                    Enviado em {format(new Date(horario.reminderSentAt), "dd/MM 'às' HH:mm")}
                  </Info>
                )}
                {podeEditar && marcado && podeMarcarResultado && (
                  <div style={{ marginTop: 10 }}>
                    <div className={classes.secaoSub} style={{ marginBottom: 6 }}>
                      O candidato veio na entrevista?
                    </div>
                    <div className={classes.botoesResultado}>
                      <Button
                        size="small"
                        className={`${classes.secundario} ${
                          horario.outcome === "compareceu" ? classes.resultadoAtivo : ""
                        }`}
                        disabled={salvando}
                        onClick={() =>
                          executar(
                            { outcome: horario.outcome === "compareceu" ? null : "compareceu" },
                            "Resultado salvo."
                          )
                        }
                      >
                        ✅ Compareceu
                      </Button>
                      <Button
                        size="small"
                        className={`${classes.secundario} ${horario.outcome === "faltou" ? classes.faltouAtivo : ""}`}
                        disabled={salvando}
                        onClick={() =>
                          executar(
                            { outcome: horario.outcome === "faltou" ? null : "faltou" },
                            "Resultado salvo."
                          )
                        }
                      >
                        ❌ Faltou
                      </Button>
                    </div>
                    <div style={{ fontSize: "0.72rem", opacity: 0.7, marginTop: 6 }}>
                      Fica salvo no candidato (aba Candidatos) e aplica a etiqueta automática de
                      "Compareceu" ou "Faltou", se você tiver criado.
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={classes.secao}>
              <div className={classes.secaoTopo}>
                <div className={classes.secaoIcone}>
                  <EventNoteRoundedIcon />
                </div>
                <div className={classes.secaoTitulo}>Horário</div>
              </div>
              <Info rotulo="Data">
                {horario.startAt ? format(new Date(horario.startAt), "EEEE, dd/MM/yyyy", { locale: ptBR }) : ""}
              </Info>
              <Info rotulo="Horário">
                {horario.startAt
                  ? `${format(new Date(horario.startAt), "HH:mm")}${
                      horario.endAt ? ` às ${format(new Date(horario.endAt), "HH:mm")}` : ""
                    }${
                      horario.endAt
                        ? ` (${Math.round((new Date(horario.endAt) - new Date(horario.startAt)) / 60000)} min)`
                        : ""
                    }`
                  : ""}
              </Info>
              <Info rotulo="Formato">{horario.location ? horario.location : "Online (link enviado pelo RH)"}</Info>
              <Info rotulo="Tipo">{horario.title}</Info>
              <Info rotulo="Entrevistador">{nomeEntrevistador}</Info>
              <Info rotulo="Observações">{horario.notes}</Info>
              {horario.bookedAt && (
                <Info rotulo="Marcada em">{format(new Date(horario.bookedAt), "dd/MM 'às' HH:mm", { locale: ptBR })}</Info>
              )}
            </div>
          </>
        )}

        {modo === "editar" && (
          <div className={classes.secao}>
            <div className={classes.grade3}>
              <TextField
                label="Data"
                type="date"
                size="small"
                variant="outlined"
                className={classes.campo}
                value={campos.data}
                onChange={mudar("data")}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Das"
                type="time"
                size="small"
                variant="outlined"
                className={classes.campo}
                value={campos.horaInicio}
                onChange={mudar("horaInicio")}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Até"
                type="time"
                size="small"
                variant="outlined"
                className={classes.campo}
                value={campos.horaFim}
                onChange={mudar("horaFim")}
                InputLabelProps={{ shrink: true }}
              />
            </div>
            <div className={classes.grade} style={{ marginTop: 12 }}>
              <TextField
                label="Tipo de entrevista"
                size="small"
                variant="outlined"
                className={classes.campo}
                value={campos.title}
                onChange={mudar("title")}
              />
              <TextField
                select
                label="Entrevistador" SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                size="small"
                variant="outlined"
                className={classes.campo}
                value={campos.interviewerId}
                onChange={mudar("interviewerId")}
              >
                <MenuItem value="">Não definido</MenuItem>
                {(usuarios || []).map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <TextField
              label="Local ou link"
              size="small"
              variant="outlined"
              fullWidth
              className={classes.campo}
              style={{ marginTop: 12 }}
              value={campos.location}
              onChange={mudar("location")}
            />
            <TextField
              label="Observações internas"
              size="small"
              variant="outlined"
              fullWidth
              multiline
              minRows={2}
              className={classes.campo}
              style={{ marginTop: 12, marginBottom: marcado ? 12 : 0 }}
              value={campos.notes}
              onChange={mudar("notes")}
            />
            {marcado && camposDoCandidato}
            {marcado && horario.ticketId && (
              <FormControlLabel
                style={{ marginTop: 8 }}
                control={<Checkbox color="primary" checked={avisar} onChange={(e) => setAvisar(e.target.checked)} />}
                label="Se mudar o horário, avisar o candidato no WhatsApp"
              />
            )}
          </div>
        )}

        {modo === "agendar" && (
          <div className={classes.secao}>
            <div className={classes.secaoSub} style={{ marginBottom: 12 }}>
              Agendamento manual (ex.: candidato que ligou). A assistente não envia mensagem neste caso.
            </div>
            {camposDoCandidato}
          </div>
        )}

        {modo === "cancelar" && (
          <div className={classes.secao}>
            <div className={classes.secaoTitulo} style={{ marginBottom: 8 }}>
              Cancelar a entrevista de {horario.candidateName}?
            </div>
            <div className={classes.secaoSub} style={{ marginBottom: 10 }}>
              O horário volta a ficar livre para outros candidatos.
            </div>
            {horario.ticketId ? (
              <>
                <FormControlLabel
                  control={
                    <Checkbox color="primary" checked={avisar} onChange={(e) => setAvisar(e.target.checked)} />
                  }
                  label="Avisar o candidato no WhatsApp"
                />
                {avisar && (
                  <TextField
                    label="Mensagem (opcional)"
                    placeholder="Em branco: mensagem padrão oferecendo remarcar."
                    size="small"
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={3}
                    className={classes.campo}
                    value={mensagem}
                    onChange={(e) => setMensagem(e.target.value)}
                  />
                )}
              </>
            ) : (
              <div className={classes.secaoSub}>Agendamento manual: avise o candidato por fora.</div>
            )}
          </div>
        )}
      </div>

      <div className={classes.rodape}>
        {modo === "ver" && (
          <>
            {podeEditar && (
              <Button className={classes.perigo} onClick={() => setConfirmarExclusao(true)}>
                Excluir
              </Button>
            )}
            <div style={{ flex: 1 }} />
            {marcado && horario.ticketId && (
              <Button
                className={classes.secundario}
                startIcon={<WhatsAppIcon />}
                onClick={() => history.push(`/atendimentos/${horario.ticketId}`)}
              >
                Conversa
              </Button>
            )}
            {podeEditar && marcado && !passou && (
              <Button className={classes.secundario} onClick={() => setModo("cancelar")}>
                Cancelar entrevista
              </Button>
            )}
            {podeEditar && !marcado && !passou && (
              <Button
                className={classes.secundario}
                disabled={salvando}
                onClick={() =>
                  executar(
                    { action: horario.status === "blocked" ? "unblock" : "block" },
                    horario.status === "blocked" ? "Horário liberado." : "Horário bloqueado."
                  )
                }
              >
                {horario.status === "blocked" ? "Desbloquear" : "Bloquear"}
              </Button>
            )}
            {podeEditar && (
              <Button className={classes.secundario} startIcon={<EditRoundedIcon />} onClick={() => setModo("editar")}>
                Editar
              </Button>
            )}
            {podeEditar && horario.status === "available" && !passou && (
              <Button className={classes.primario} onClick={() => setModo("agendar")}>
                Agendar candidato
              </Button>
            )}
          </>
        )}
        {modo !== "ver" && (
          <>
            <div style={{ flex: 1 }} />
            <Button className={classes.secundario} onClick={() => setModo("ver")} disabled={salvando}>
              Voltar
            </Button>
            {modo === "editar" && (
              <Button className={classes.primario} onClick={salvarEdicao} disabled={salvando}>
                Salvar
              </Button>
            )}
            {modo === "agendar" && (
              <Button
                className={classes.primario}
                onClick={agendarManual}
                disabled={salvando || !campos.candidateName.trim()}
              >
                Agendar
              </Button>
            )}
            {modo === "cancelar" && (
              <Button className={classes.primario} onClick={cancelarEntrevista} disabled={salvando}>
                Confirmar cancelamento
              </Button>
            )}
          </>
        )}
      </div>

      <ConfirmationModal
        title="Excluir este horário?"
        open={confirmarExclusao}
        onClose={() => setConfirmarExclusao(false)}
        onConfirm={excluir}
      >
        {marcado
          ? "Este horário tem entrevista marcada. Excluir NÃO avisa o candidato — prefira “Cancelar entrevista”."
          : "O horário sai da agenda e a assistente deixa de oferecê-lo."}
      </ConfirmationModal>
    </Dialog>
  );
};

export default DetalheHorarioModal;
